import React, { useEffect, useMemo, useRef } from "react"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate, useLocation } from "react-router-dom"
import GetSpace from "../../utils/space"
import { AuxNavBar } from "../Navbar/Navbar"
import { getDateRangeSearch } from "../../utils/dateRangeSearchParams"
import { useCompanyModulesStatus } from "../../context/useCompanyModulesStatusContext"

interface MobileProps {
  menus: AuxNavBar[]
}

export const MobileBottomNavbar: React.FC<MobileProps> = ({ menus }) => {
  const navigate = useNavigate()

  const location = useLocation()

  const { userEmployee } = useEmployerLogged()

  const { isModuleEnabled } = useCompanyModulesStatus()

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const activeButtonRef = useRef<HTMLButtonElement | null>(null)

  const availableMenus = useMemo(
    () =>
      menus.filter(
        (menu) =>
          (!menu.permission || userEmployee?.permissions?.includes(menu.permission)) && isModuleEnabled(menu.module),
      ),
    [menus, userEmployee, isModuleEnabled],
  )

  useEffect(() => {
    if (!activeButtonRef.current || !scrollContainerRef.current) {
      return
    }

    const animationFrame = window.requestAnimationFrame(() => {
      activeButtonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      })
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [availableMenus, location.pathname])

  const handleClick = (menu: AuxNavBar) => {
    navigate(`${menu.path}${getDateRangeSearch(location.search)}`, { state: menu.params })
  }

  return (
    <div className="lg:hidden fixed inset-x-0 bottom-0 z-50">
      <div
        className="
          bg-white/95 backdrop-blur-xl
          border-t-2 border-orange-100
          px-2 pt-2 pb-[max(env(safe-area-inset-bottom),0.5rem)]
          w-full
        "
      >
        <div
          ref={scrollContainerRef}
          className={`
            overflow-x-auto
            flex gap-1
            snap-x snap-mandatory
            scrollbar-none
            mb-2
            mx-14
            ${availableMenus.length <= 2 ? "justify-center" : ""}
          `}
        >
          {availableMenus.map((menu, i) => {
            const isActive = location.pathname === menu.path

            return (
              <button
                key={i}
                ref={isActive ? activeButtonRef : undefined}
                onClick={() => handleClick(menu)}
                className={`
                   snap-center
                    flex-none min-w-[115px]
                    flex flex-col items-center justify-center
                    
                    px-3 
                    transition-all duration-300
                   
                  `}
                data-interaction-description-key={menu.mobileDescriptionKey}
              >
                <span
                  className={`
                    flex h-9 w-9 items-center justify-center rounded-xl transition-colors
                    ${isActive ? "bg-orange-200 text-orange-700" : "bg-gray-100 text-gray-500"}
                  `}
                >
                  <FontAwesomeIcon icon={menu.icon} className="text-sm" />
                </span>

                <GetSpace top={2} />

                <span
                  className={`text-[10px] mt-1 font-black uppercase tracking-widest text-center leading-tight ${isActive ? "text-orange-700" : "text-gray-500"}`}
                >
                  {menu.title}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
